import React, { useState } from "react";
import { AlertTriangle, X, Flame } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function DangerZoneReset({ onDone }) {
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset", { confirm });
      const d = data.deleted || {};
      toast.success(
        `Reset complete — wiped ${d.questions || 0} questions, ${d.batches || 0} batches, ${d.tags || 0} tags, ${d.users || 0} users`
      );
      setOpen(false);
      setConfirm("");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        data-testid="danger-zone"
        className="mt-10 rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/40 p-6"
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-bold text-rose-900" style={{ fontFamily: "Outfit" }}>
              Danger Zone · Testing Only
            </h3>
            <p className="text-sm text-rose-800/80 mt-1 leading-relaxed">
              Wipe all questions, batches, tags, and every user except the Super Admin. This
              action cannot be undone — use it to reset the environment between test runs.
            </p>
          </div>
          <button
            data-testid="reset-open-btn"
            onClick={() => setOpen(true)}
            className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
          >
            <Flame className="w-4 h-4" />
            Reset all data
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div
            data-testid="reset-modal"
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold text-rose-700 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
                <AlertTriangle className="w-5 h-5" />
                Confirm full reset
              </h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed">
              This will permanently delete:
            </p>
            <ul className="mt-2 mb-4 text-sm text-slate-700 list-disc list-inside space-y-0.5">
              <li>All raw questions</li>
              <li>All batch assignments &amp; reviews</li>
              <li>All tags</li>
              <li>All users <strong>except the Super Admin</strong></li>
            </ul>
            <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Type <code className="text-rose-700">RESET</code> to continue
            </label>
            <input
              data-testid="reset-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoFocus
              className="w-full px-3 py-2 rounded-lg border-2 border-rose-200 text-sm font-mono focus:border-rose-500 focus:ring-2 focus:ring-rose-500/20 outline-none"
              placeholder="RESET"
            />
            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                data-testid="reset-confirm-btn"
                onClick={run}
                disabled={busy || confirm !== "RESET"}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold"
              >
                <Flame className="w-4 h-4" />
                {busy ? "Resetting…" : "Wipe Everything"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
